import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useUser } from "@/context/UserContext";
import api from "@/utils/api";
import { User, Phone } from "lucide-react";

/**
 * Modal that appears after login if a client account has no name set.
 *
 * Why: When a request is sent to IO via webhook, IO displays the client's
 * name. If empty, IO shows "Unknown customer". We force-complete the profile
 * the first time the user lands on a protected page after login.
 *
 * Interpreters are not affected — their name is managed in /dolmetscher/profil.
 */
export default function ProfileCompletionGate() {
  const { user, updateUser } = useUser();
  const [needs, setNeeds] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user || user.user_type !== "client") return;
    // 1. user.name from auth context — most reliable
    if (user.name && user.name.trim()) return;
    // 2. Fetch client_profiles in case name is set there but not yet in context
    api.get(`/client-profiles/${user.id}`)
      .then((r) => {
        if (r.data?.name && r.data.name.trim()) {
          updateUser({ name: r.data.name });
        } else {
          setNeeds(true);
        }
      })
      .catch(() => setNeeds(true)); // no profile yet → must complete
  }, [user, updateUser]);

  if (!needs) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) { setError("Bitte gib deinen Namen ein."); return; }
    setSaving(true);
    try {
      await api.put(`/client-profiles/${user.id}`, {
        name: form.name.trim(),
        phone: form.phone.trim() || undefined,
      });
      updateUser({ name: form.name.trim() });
      setNeeds(false);
    } catch {
      setError("Konnte nicht gespeichert werden. Bitte erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      data-testid="profile-completion-gate"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#14B8A6] to-[#0D9488] rounded-2xl flex items-center justify-center text-white">
            <User size={22} />
          </div>
          <div>
            <h2 className="font-bold text-gray-900 text-lg">Willkommen!</h2>
            <p className="text-xs text-gray-500">Vervollständige dein Profil, um Anfragen senden zu können.</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vollständiger Name <span className="text-red-500">*</span>
            </label>
            <input
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Max Mustermann"
              data-testid="gate-name-input"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Dieser Name wird Dolmetscher:innen bei jeder Anfrage angezeigt.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefon <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+49 30 1234567"
                data-testid="gate-phone-input"
                className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-xl" data-testid="gate-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={saving}
            data-testid="gate-submit"
            className="w-full py-3 bg-[#14B8A6] hover:bg-[#0D9488] text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-50"
          >
            {saving ? "Wird gespeichert…" : "Weiter"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
